import {Component, Inject, OnInit} from '@angular/core';
import {BehaviorSubject, Observable, tap} from 'rxjs';
import {SuppliersService} from '../../../services/suppliers.service';
import {ActivatedRoute} from '@angular/router';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {CreateAddressComponent} from '../create-address/create-address.component';
import {CreateContactComponent} from '../create-contact/create-contact.component';
import {CreateConnectionComponent} from '../create-connection/create-connection.component';
import {APP_GUIDS, AppGuids} from '../../../app.guids';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmDialogComponent} from '../../dialogs/confirm-dialog.component';
import {TranslateService} from '@ngx-translate/core';
import {CoreService, ProleisObject, SupplierContactsService} from "@proleis/proleis-rest-client";
import {NotificationService} from "@proleis/proleis-web-app";

@Component({
  selector: 'ps-contat-details',
  templateUrl: 'contacts.details.component.html',
  styleUrls: ['contacts.details.component.scss']
})
export class ContactsDetailsComponent implements OnInit {

  countries$: Observable<ProleisObject[] | undefined>;
  addressTypes$: Observable<ProleisObject[] | undefined>;
  connectionTypes$: Observable<ProleisObject[] | undefined>;
  supplier$: Observable<ProleisObject>;
  contacts$: Observable<ProleisObject[]>;
  contactEdited$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  selectedContact$: BehaviorSubject<ProleisObject | undefined> = new BehaviorSubject<ProleisObject | undefined>(undefined);
  contactConnections$: Observable<ProleisObject[] | undefined>;
  selectedContactConnection$: BehaviorSubject<ProleisObject | undefined> = new BehaviorSubject<ProleisObject | undefined>(undefined);
  addresses$: Observable<ProleisObject[]>;
  selectedAddress$: BehaviorSubject<ProleisObject | undefined> = new BehaviorSubject<ProleisObject | undefined>(undefined);
  connections$: Observable<ProleisObject[]>;
  selectedConnection$: BehaviorSubject<ProleisObject | undefined> = new BehaviorSubject<ProleisObject | undefined>(undefined);

  constructor(private suppliersService: SuppliersService, private suppliersContactsService: SupplierContactsService, private cs: CoreService,
              private route: ActivatedRoute, private bottomSheet: MatBottomSheet, private ns: NotificationService,
              @Inject(APP_GUIDS) private guid: AppGuids, private dialog: MatDialog, private translate: TranslateService) {
  }

  ngOnInit() {
    this.loadAddresses();
    this.loadSuppliers();
    this.loadContacts();
    this.loadConnections();

    this.countries$ = this.suppliersContactsService.getCountries();
    this.connectionTypes$ = this.suppliersContactsService.getConnectionTypes();
    this.addressTypes$ = this.suppliersContactsService.getAddressTypes();
  }

  loadContacts() {
    const aliasFields = 'TYPEGUID=TYP_ID.GUID';
    this.contacts$ = this.suppliersContactsService.getSupplierContacts(this.route.snapshot.params.supplierId, aliasFields).pipe(
      tap(contacts => this.selectedContact$.next(contacts[0]))
    );
  }

  loadSuppliers() {
    const aliasFields = 'TYPNAME=ADR_TYP_ID.DESCRIPTION;RESPNAME=RESPONSIBLE.TEXT;TYPEGUID=TYP_ID.GUID';
    this.supplier$ = this.cs.getObjectById(this.route.snapshot.params.supplierId, aliasFields);
  }

  loadAddresses() {
    const aliasFields = 'ADDRESSTYPENAME=ANSCH_TYP_ID.TEXT;LANDNAME=LAND_ID.DESCRIPTION';
    this.addresses$ = this.suppliersContactsService.getSupplierAddresses(this.route.snapshot.params.supplierId, aliasFields);
  }

  loadConnections() {
    const aliasFields = 'CONNECTIONTYPENAME=CONNECT_TYP_ID.NAME;TYPEGUID=TYP_ID.GUID';
    this.connections$ = this.suppliersContactsService.getSupplierConnections(this.route.snapshot.params.supplierId, this.route.snapshot.params.supplierId, aliasFields);
  }

  selectContact(contact: ProleisObject) {
    this.selectedContact$.next(contact);
    this.contactConnections$ = this.getContactConnections(contact);
  }

  selectContactConnection(connection: ProleisObject) {
    this.selectedContactConnection$.next(connection);
  }

  selectConnection(connection: ProleisObject) {
    this.selectedConnection$.next(connection);
  }

  selectAddress(address: ProleisObject) {
    this.selectedAddress$.next(address);
  }

  contactChanged() {
    this.contactEdited$.next(true);
  }

  updateContact(contact: ProleisObject) {
    this.suppliersContactsService.updateSupplierContact(this.route.snapshot.params.supplierId, contact.OBJECT_ID, {
      lastName: contact.DESCRIPTION,
      firstName: contact.NAME
    }).subscribe();
  }

  updateConnection(parent: ProleisObject, connection: ProleisObject) {
    this.suppliersContactsService.updateSupplierConnection(this.route.snapshot.params.supplierId, parent.OBJECT_ID, connection.ADR_CONNECT_ID, {
      info: connection.INFO,
      text: connection.TEXT,
      typeId: connection.CONNECT_TYP_ID
    }).subscribe();
  }

  updateAddress(address: ProleisObject) {
    this.suppliersContactsService.updateSupplierAddress(this.route.snapshot.params.supplierId, address.ADR_ANSCHRIFTEN_ID, {
      typeId: address.TYP_ID,
      city: address.STADT,
      countryId: address.LAND_ID,
      poBox: address.POSTFACH,
      street: address.STRASSE,
      description: address.DESCRIPTION,
      poastalCode: address.PLZSTR
    }).subscribe(result => {
      this.ns.showNotification(this.translate.instant('contacts.notifications.addressUpdated'));
    });
  }

  createContact() {
    this.bottomSheet.open(CreateContactComponent).afterDismissed().subscribe(result => {
      if (result) {
        this.suppliersContactsService.createSupplierContact(this.route.snapshot.params.supplierId, {
          lastName: result.lastName,
          firstName: result.firstName
        }).subscribe(result => {
          this.ns.showNotification(this.translate.instant('contacts.notifications.contactAdded'));
          this.loadContacts();
        });
      }
    });
  }

  createAddress() {
    this.bottomSheet.open(CreateAddressComponent).afterDismissed().subscribe(result => {
      if (result) {
        this.suppliersContactsService.createSupplierAddress(this.route.snapshot.params.supplierId, result).subscribe(res => {
          this.ns.showNotification('Adresse angelegt');
          this.loadAddresses();
        });
      }
    });
  }

  createConnection(parent: ProleisObject) {
    this.bottomSheet.open(CreateConnectionComponent).afterDismissed().subscribe(result => {
      if (result) {
        this.suppliersContactsService.createSupplierConnection(this.route.snapshot.params.supplierId, parent.OBJECT_ID, {
          typeId: result.typeId,
          text: result.text,
          info: result.info
        }).subscribe(result => {
          switch (result.TYPEGUID) {
            case this.guid.GUID_TYPE_CONTACT:
              this.contactConnections$ = this.getContactConnections(parent);
              break;
            case this.guid.GUID_TYPE_SUPPLIER:
              this.loadConnections();
              break;
          }
          this.ns.showNotification('Verbindung angelegt!');
        });
      }
    });
  }

  deleteContact(contact: ProleisObject) {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Kontakt löschen',
        message: 'Möchten Sie diesen Kontakt wirklich löschen?'
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.suppliersContactsService.deleteSupplierContact(this.route.snapshot.params.supplierId, contact.OBJECT_ID).subscribe(result => {
          this.ns.showNotification('Kontakt gelöscht!');
          this.loadContacts();
        });
      }
    });
  }

  deleteConnection(parent: ProleisObject, connection: ProleisObject) {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Verbindung löschen',
        message: 'Möchten Sie diese Verbindung wirklich löschen?'
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.suppliersContactsService.deleteSupplierConnection(this.route.snapshot.params.supplierId, parent.OBJECT_ID, connection.ADR_CONNECT_ID).subscribe(result => {
          this.ns.showNotification('Verbindung gelöscht!');
          this.loadConnections();
        });
      }
    });
  }

  deleteAddress(address: ProleisObject) {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Adresse löschen',
        message: 'Möchten Sie diese Adresse wirklich löschen?'
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.suppliersContactsService.deleteSupplierAddress(this.route.snapshot.params.supplierId, address.ADR_ANSCHRIFTEN_ID).subscribe(result => {
          this.ns.showNotification('Adresse gelöscht!');
          this.loadAddresses();
        });
      }
    });
  }

  getContactConnections(contact: ProleisObject): Observable<ProleisObject[] | undefined> {
    const aliasFields = 'CONNECT_TYP_NAME=CONNECT_TYP_ID.NAME;TYPEGUID=TYP_ID.GUID';
    return this.suppliersContactsService.getSupplierConnections(this.route.snapshot.params.supplierId, contact.OBJECT_ID, aliasFields);
  }

  compareObjects(o1: ProleisObject, o2: ProleisObject) {
    return o1.OBJECT_ID === o2.OBJECT_ID;
  }
}
